"use client";

import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import clsx from "clsx";
import {
  RefreshCw,
  Search,
  Mic,
  Clock,
  XCircle,
  AlertTriangle,
  Sparkles,
} from "lucide-react";
import { AdminShell } from "@/components/admin-shell";
import { FgCard } from "@/components/fg-card";
import { FgButton } from "@/components/fg-button";
import { FgToast } from "@/components/fg-toast";
import { api } from "@/lib/api";

type ScenarioLevel = "A1" | "A2" | "B1" | "B2" | "C1";
const LEVELS: readonly ScenarioLevel[] = ["A1", "A2", "B1", "B2", "C1"] as const;

interface Scenario {
  id: string;
  code: string;
  level: ScenarioLevel;
  titleDe: string;
  titleEn: string;
  setting: string;
  learnerGoal: string;
  aiRole: string;
  difficultyMin: number;
  difficultyMax: number;
  category: string | null;
  isActive: boolean;
  orderIndex: number;
}

interface Question {
  id: string;
  scenarioId: string;
  level: ScenarioLevel;
  difficulty: number;
  code: string;
  content: {
    questionType: string;
    promptDe: string;
    promptEn?: string;
    expectedAnswerDe: string;
    expectedAnswerEn?: string;
    hints?: string[];
    choices?: Array<{ text: string; isCorrect: boolean }>;
  };
  isApproved: boolean;
  serveCount: number;
}

/* eslint-disable @typescript-eslint/no-unused-vars */

type Tab = "scenarios" | "questions" | "sessions";

export function SpeakingPage() {
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>("scenarios");
  const [levelFilter, setLevelFilter] = useState<ScenarioLevel | "all">("all");
  const [search, setSearch] = useState("");
  const [seedResult, setSeedResult] = useState<{ inserted: number; skipped: number } | null>(null);

  // ── Scenarios ──────────────────────────────────────────────────────────
  const allScenarios = useQuery<Scenario[]>({
    queryKey: ["admin", "exam-scenarios"],
    queryFn: async () => (await api.get("/api/admin/exam-scenarios")).data,
  });
  const filteredScenarios = useMemo(() => {
    const list = allScenarios.data ?? [];
    return list.filter((s) => {
      if (levelFilter !== "all" && s.level !== levelFilter) return false;
      if (
        search &&
        !`${s.code} ${s.titleDe} ${s.titleEn} ${s.aiRole}`
          .toLowerCase()
          .includes(search.toLowerCase())
      )
        return false;
      return true;
    });
  }, [allScenarios.data, levelFilter, search]);

  // ── Questions ──────────────────────────────────────────────────────────
  const [scenarioCode, setScenarioCode] = useState("");
  const scenarioQuestions = useQuery<Question[]>({
    queryKey: ["admin", "exam-questions", scenarioCode],
    queryFn: async () => (await api.get(`/api/exams/by-scenario/${scenarioCode}`)).data,
    enabled: !!scenarioCode,
  });

  // ── Sessions tab — UI scaffold ready, endpoint ships in a follow-up patch. ─

  const seedScenarios = useMutation({
    mutationFn: async () => (await api.post("/api/admin/exam-scenarios/seed")).data,
    onSuccess: (data: { inserted: number; skipped: number; total: number }) => {
      setSeedResult({ inserted: data.inserted, skipped: data.skipped });
      qc.invalidateQueries({ queryKey: ["admin", "exam-scenarios"] });
      FgToast.success(
        `Seeded ${data.inserted} new scenarios (${data.skipped} already existed).`,
      );
    },
    onError: (e) => FgToast.error((e as { message?: string }).message ?? "Seed failed"),
  });

  return (
    <AdminShell>
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-[rgba(255,255,255,0.08)] pb-6 mb-8"
      >
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Mic className="w-5 h-5 text-[#FFD60A]" />
            <span className="text-[11px] font-mono font-bold tracking-widest uppercase text-[#FFD60A]">
              AI SPEAKING & EXAM
            </span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-[#FAFAFA]">
            Speaking Practice &amp; Goethe Exam
          </h1>
          <p className="mt-1 text-sm text-[#8E8E93]">
            250 scenarios · Gemini-powered question generator · realtime voice sessions
          </p>
        </div>
        <FgButton
          size="sm"
          variant="primary"
          onClick={() => seedScenarios.mutate()}
          loading={seedScenarios.isPending}
          leftIcon={<Sparkles className="w-4 h-4" />}
        >
          {seedResult ? "Re-seed (idempotent)" : "Seed all 250 scenarios"}
        </FgButton>
      </motion.div>

      {/* Tabs */}
      <div className="mb-6 inline-flex items-center gap-1 p-1 rounded-xl bg-[#1C1C1E]/60 border border-[rgba(255,255,255,0.08)] backdrop-blur">
        {(
          [
            { id: "scenarios" as const, label: "Scenarios" },
            { id: "questions" as const, label: "Question Bank" },
            { id: "sessions" as const, label: "Sessions" },
          ] as const
        ).map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={clsx(
              "relative px-3.5 py-1.5 rounded-lg text-xs font-bold transition-colors",
              tab === t.id ? "text-[#0A0A0A]" : "text-[#8E8E93] hover:text-[#FAFAFA]",
            )}
          >
            {tab === t.id && (
              <motion.div
                layoutId="speaking-tab"
                className="absolute inset-0 bg-[#FFD60A] rounded-lg"
                transition={{ type: "spring", stiffness: 500, damping: 30 }}
              />
            )}
            <span className="relative z-10">{t.label}</span>
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={tab}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.2 }}
        >
          {tab === "scenarios" && (
            <ScenariosPanel
              scenarios={filteredScenarios}
              levelFilter={levelFilter}
              setLevelFilter={setLevelFilter}
              search={search}
              setSearch={setSearch}
              loading={allScenarios.isLoading}
              onRefresh={() => allScenarios.refetch()}
            />
          )}
          {tab === "questions" && (
            <QuestionsPanel
              scenarioCode={scenarioCode}
              setScenarioCode={setScenarioCode}
              scenarios={allScenarios.data ?? []}
              questions={scenarioQuestions.data ?? []}
              loading={scenarioQuestions.isLoading}
              onRefresh={() => scenarioQuestions.refetch()}
            />
          )}
          {tab === "sessions" && <SessionsPanel />}
        </motion.div>
      </AnimatePresence>
    </AdminShell>
  );
}

function ScenariosPanel({
  scenarios,
  levelFilter,
  setLevelFilter,
  search,
  setSearch,
  loading,
  onRefresh,
}: {
  scenarios: Scenario[];
  levelFilter: ScenarioLevel | "all";
  setLevelFilter: (v: ScenarioLevel | "all") => void;
  search: string;
  setSearch: (v: string) => void;
  loading: boolean;
  onRefresh: () => void;
}) {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#8E8E93]" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by code, title, AI role…"
            className="w-full h-9 rounded-lg border border-[rgba(255,255,255,0.1)] bg-[#1C1C1E] pl-7 pr-3 text-sm text-[#FAFAFA] placeholder:text-[#8E8E93] focus:outline-none focus:border-[#FFD60A]"
          />
        </div>
        <div className="inline-flex items-center gap-1 p-1 rounded-lg bg-[#1C1C1E]/60 border border-[rgba(255,255,255,0.08)]">
          {(["all", ...LEVELS] as const).map((l) => (
            <button
              key={l}
              onClick={() => setLevelFilter(l)}
              className={clsx(
                "relative px-2.5 py-1 rounded text-[10px] font-bold uppercase tracking-wider transition-colors",
                levelFilter === l ? "text-[#0A0A0A]" : "text-[#8E8E93] hover:text-[#FAFAFA]",
              )}
            >
              {levelFilter === l && (
                <motion.span
                  layoutId="level-filter-tab"
                  className="absolute inset-0 bg-[#FFD60A] rounded"
                  transition={{ type: "spring", stiffness: 500, damping: 30 }}
                />
              )}
              <span className="relative z-10">{l}</span>
            </button>
          ))}
        </div>
        <FgButton
          size="sm"
          variant="secondary"
          onClick={onRefresh}
          leftIcon={<RefreshCw className="w-4 h-4" />}
        >
          Refresh
        </FgButton>
      </div>

      {loading && (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-16 rounded-xl bg-white/5 fg-skeleton" />
          ))}
        </div>
      )}

      {!loading && scenarios.length === 0 && (
        <FgCard className="p-10 text-center">
          <Mic className="w-10 h-10 mx-auto text-[#8E8E93] mb-3" />
          <p className="text-sm text-[#FAFAFA] font-semibold">No scenarios yet</p>
          <p className="mt-1 text-xs text-[#8E8E93]">
            Click <em>Seed all 250 scenarios</em> to load the curated library.
          </p>
        </FgCard>
      )}

      {!loading && scenarios.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
          {scenarios.map((s, idx) => (
            <motion.div
              key={s.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: Math.min(idx, 12) * 0.02 }}
            >
              <FgCard className="p-3.5">
                <div className="flex items-start gap-3">
                  <span className="font-mono text-[11px] font-bold bg-[#FFD60A] text-[#0A0A0A] px-2 py-0.5 rounded shrink-0">
                    {s.level}
                  </span>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-bold text-[#FAFAFA] truncate">
                      {s.titleDe}
                    </h3>
                    <p className="text-[11px] text-[#8E8E93] truncate">
                      {s.titleEn}
                    </p>
                    <div className="mt-1 flex items-center gap-2 flex-wrap text-[10px] font-mono text-[#8E8E93]">
                      <span className="inline-flex items-center gap-1 rounded-full bg-white/5 px-1.5 py-0.5">
                        <Clock size={9} /> {s.aiRole}
                      </span>
                      <span className="inline-flex items-center gap-1 rounded-full bg-white/5 px-1.5 py-0.5">
                        D{s.difficultyMin}–D{s.difficultyMax}
                      </span>
                      {s.category && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-white/5 px-1.5 py-0.5">
                          {s.category}
                        </span>
                      )}
                      {!s.isActive && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-[#8E8E93]/15 text-[#8E8E93] px-1.5 py-0.5">
                          <XCircle size={9} /> inactive
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </FgCard>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}

function QuestionsPanel({
  scenarioCode,
  setScenarioCode,
  scenarios,
  questions,
  loading,
  onRefresh,
}: {
  scenarioCode: string;
  setScenarioCode: (v: string) => void;
  scenarios: Scenario[];
  questions: Question[];
  loading: boolean;
  onRefresh: () => void;
}) {
  return (
    <div className="space-y-4">
      <FgCard className="p-5">
        <h2 className="text-sm font-bold text-[#FAFAFA] mb-3">Browse base questions</h2>
        <p className="text-xs text-[#8E8E93] mb-3">
          Hand-authored base questions are the templates Gemini uses to generate
          fresh variants. Pick a scenario to inspect its bank.
        </p>
        <select
          value={scenarioCode}
          onChange={(e) => setScenarioCode(e.target.value)}
          className="w-full h-9 rounded-lg border border-[rgba(255,255,255,0.1)] bg-[#1C1C1E] px-3 text-sm text-[#FAFAFA] focus:outline-none focus:border-[#FFD60A]"
        >
          <option value="">— Pick a scenario —</option>
          {scenarios.map((s) => (
            <option key={s.code} value={s.code}>
              {s.level} · {s.code} · {s.titleDe}
            </option>
          ))}
        </select>
      </FgCard>

      {scenarioCode && (
        <FgCard className="p-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs text-[#8E8E93]">
              {loading ? "Loading…" : `${questions.length} approved questions`}
            </p>
            <FgButton
              size="sm"
              variant="ghost"
              onClick={onRefresh}
              leftIcon={<RefreshCw className="w-3.5 h-3.5" />}
            >
              Refresh
            </FgButton>
          </div>
          {questions.length === 0 && !loading && (
            <p className="text-xs text-[#8E8E93] text-center py-6">
              No base questions yet for this scenario. Use the seed script to add them.
            </p>
          )}
          <div className="space-y-2">
            {questions.map((q) => (
              <FgCard key={q.id} className="p-3">
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="font-mono text-[10px] bg-[#FFD60A] text-[#0A0A0A] px-1.5 py-0.5 rounded">
                    D{q.difficulty}
                  </span>
                  <span className="font-mono text-[10px] bg-white/5 px-1.5 py-0.5 rounded uppercase text-[#8E8E93]">
                    {q.content.questionType}
                  </span>
                  <span className="text-[10px] text-[#8E8E93]">
                    served {q.serveCount}×
                  </span>
                </div>
                <p className="text-sm text-[#FAFAFA]">{q.content.promptDe}</p>
                <p className="mt-1 text-[11px] text-[#8E8E93]">
                  Expected: {q.content.expectedAnswerDe}
                </p>
              </FgCard>
            ))}
          </div>
        </FgCard>
      )}
    </div>
  );
}

function SessionsPanel() {
  return (
    <FgCard className="p-5">
      <h2 className="text-sm font-bold text-[#FAFAFA] mb-3">Speaking sessions</h2>
      <p className="text-xs text-[#8E8E93]">
        Per-session view of every AI speaking / Goethe exam attempt. Filter by user,
        scenario, or status. The backend already logs every session — admin
        listing endpoint ships in a follow-up patch.
      </p>
      <p className="mt-3 text-xs text-[#FFD60A] flex items-center gap-1.5">
        <AlertTriangle size={11} /> Coming soon — for now inspect via SQL.
      </p>
    </FgCard>
  );
}
